import  { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Lock, User, Eye, EyeOff, ArrowLeft, Radio, Users, Settings, AlertCircle, CheckCircle, Shield, Truck } from 'lucide-react';

function AdminLogin() {
  const [formData, setFormData] = useState({ username: '', password: '', role: 'staff' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isCheckingAuth = useRef(false); // flag ca să nu ruleze de mai multe ori

  const parseJwt = (token) => {
    try {
      const base64Payload = token.split('.')[1];
      const payload = atob(base64Payload);
      return JSON.parse(payload);
    } catch {
      return null;
    }
  };

  useEffect(() => {
  if (isCheckingAuth.current) return;

  isCheckingAuth.current = true;

  const storedUser = localStorage.getItem('user');
  const storedToken = localStorage.getItem('token');
  const storedExternalToken = localStorage.getItem('externalToken');

  if (storedUser && storedToken) {
    const user = JSON.parse(storedUser);
    const payload = parseJwt(storedToken);

    if (payload && payload.exp * 1000 > Date.now()) {
      // aici poți extinde login să accepte și token extern (dacă ai definit)
      login(user, storedToken, storedExternalToken);

      if (user.role === 'admin') {
        navigate('/admin', { replace: true });
      } else if (user.role === 'dispacer') {
        navigate('/dispacer', { replace: true });
      } else if (user.role === 'sofer') {
        navigate('/sofer', { replace: true });
      } else {
        const from = location.state?.from || '/';
        navigate(from, { replace: true });
      }
    } else {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('externalToken');
    }
  }
}, [login, navigate, location.state]);




  const from = location.state?.from || '/';

  const handleSubmit = async () => {
  if (!formData.username || !formData.password) {
    setError('Toate câmpurile sunt obligatorii');
    return;
  }
  if (isLoading) return;

  setError('');
  setIsLoading(true);
  setSuccess('');

  try {
    const response = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    });

    const data = await response.json();

    if (response.ok && data.success) {
      if (
        (formData.role === 'admin' && data.user.role !== 'admin') ||
        (formData.role === 'staff' && data.user.role === 'admin')
      ) {
        setError('Contul nu este autorizat pentru acest tip de acces.');
        setIsLoading(false);
        return;
      }

      // Salvează în localStorage tokenul intern și tokenul extern primit
      localStorage.setItem('token', data.token);
      if (data.externalToken) {
        localStorage.setItem('externalToken', data.externalToken);
      }

      // ✅ FIX: Pasează externalToken către funcția login
      login(data.user, data.token, data.externalToken);
      setSuccess(data.message);

      setTimeout(() => {
        if (data.user.role === 'admin') {
          navigate('/admin', { replace: true });
        } else if (data.user.role === 'dispacer') {
          navigate('/dispacer', { replace: true });
        } else if (data.user.role === 'sofer') {
          navigate('/sofer', { replace: true });
        } else {
          navigate(from, { replace: true });
        }
      }, 1500);
    } else {
      setError(data.error || 'Eroare la autentificare');
    }
  } catch {
    setError('Eroare de conexiune. Verificați conexiunea la internet.');
  } finally {
    setIsLoading(false);
  }
};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-800 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 md:w-96 h-64 md:h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 md:w-96 h-64 md:h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-3/4 left-3/4 w-48 md:w-64 h-48 md:h-64 bg-green-500/10 rounded-full blur-3xl"></div>
      </div>

      {/* Back Button */}
      <a href="/" className="absolute top-4 md:top-6 left-4 md:left-6 flex items-center text-white/80 hover:text-white transition-colors group z-20">
        <ArrowLeft className="w-4 h-4 md:w-5 md:h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
        <span className="text-xs md:text-sm font-medium">Înapoi la site</span>
      </a>

      {/* Main Container */}
      <div className="relative z-10 w-full max-w-md mx-auto">
        {/* Header */}
        <div className="text-center mb-6 md:mb-8">
          <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-4 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center border border-white/20">
            <img 
              src="/logo.png" 
              alt="VGL Logo" 
              className="w-10 h-10 md:w-12 md:h-12 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <Truck className="w-10 h-10 md:w-12 md:h-12 text-orange-400 hidden" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
            {formData.role === 'staff' ? 'Panou Personal' : 'Panou Admin'}
          </h1>
          <p className="text-gray-300 text-sm md:text-base">VGL Trans Express</p>
        </div>

        {/* Login Form */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl md:rounded-3xl border border-white/20 shadow-2xl p-6 md:p-8">
          <div className="space-y-4 md:space-y-6">
            {/* Role Selection */}
            <div className="space-y-3">
              <label className="block text-sm font-medium text-white">Tip de cont</label>
              <div className="grid grid-cols-2 gap-2 md:gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, role: 'staff' }))}
                  className={`p-3 md:p-4 rounded-xl border transition-colors duration-200 flex flex-col items-center space-y-1 md:space-y-2 ${
                    formData.role === 'staff'
                      ? 'bg-green-500/20 border-green-500/50 text-green-300' 
                      : 'bg-white/5 border-white/20 text-gray-300 hover:bg-white/10'
                  }`}
                  disabled={isLoading}
                >
                  <div className="flex items-center space-x-1">
                    <Radio className="w-3 h-3 md:w-4 md:h-4" />
                    <Users className="w-3 h-3 md:w-4 md:h-4" />
                  </div>
                  <div className="text-center">
                    <span className="text-xs md:text-sm font-medium block">Personal VGL</span>
                    <span className="text-xs text-gray-400">Dispeceri & Șoferi</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, role: 'admin' }))}
                  className={`p-3 md:p-4 rounded-xl border transition-colors duration-200 flex flex-col items-center space-y-1 md:space-y-2 ${
                    formData.role === 'admin'
                      ? 'bg-orange-500/20 border-orange-500/50 text-orange-300'
                      : 'bg-white/5 border-white/20 text-gray-300 hover:bg-white/10'
                  }`}
                  disabled={isLoading}
                >
                  <Settings className="w-4 h-4 md:w-5 md:h-5" />
                  <div className="text-center">
                    <span className="text-xs md:text-sm font-medium block">Administrator</span>
                    <span className="text-xs text-gray-400">Acces complet</span>
                  </div>
                </button>
              </div>
            </div>

            {/* Username Input */}
            <div className="space-y-2">
              <label htmlFor="username" className="block text-sm font-medium text-white">Nume de utilizator</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                <input
                  id="username"
                  name="username"
                  type="text"
                  value={formData.username}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, username: e.target.value }));
                    setError('');
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleSubmit()}
                  className="w-full pl-10 pr-3 py-3 border border-white/20 rounded-xl bg-white/10 backdrop-blur-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                  placeholder="Introduceți numele de utilizator"
                  disabled={isLoading}
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-white">Parolă</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, password: e.target.value }));
                    setError('');
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && !isLoading && handleSubmit()}
                  className="w-full pl-10 pr-12 py-3 border border-white/20 rounded-xl bg-white/10 backdrop-blur-sm text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent transition-colors"
                  placeholder="Introduceți parola"
                  disabled={isLoading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors focus:outline-none"
                  disabled={isLoading}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="flex items-center space-x-2 text-red-400 bg-red-500/10 border-red-500/20 backdrop-blur-sm border rounded-xl p-3">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {success && (
              <div className="flex items-center space-x-2 text-green-400 bg-green-500/10 border-green-500/20 backdrop-blur-sm border rounded-xl p-3">
                <CheckCircle className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm">{success}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
                onClick={handleSubmit}
                disabled={isLoading}
                className={`w-full font-bold py-3 px-6 rounded-xl ${
                  formData.role === 'staff'
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500'
                    : 'bg-gradient-to-r from-orange-500 to-red-500'
                } text-white`}
              >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span className="text-sm md:text-base">Se autentifică...</span>
                </>
              ) : (
                <span className="text-sm md:text-base">
                  {formData.role === 'staff' ? 'Intrare Personal VGL' : 'Intrare Admin'}
                </span>
              )}
            </button>
          </div>

          {/* Additional Info */}
          <div className="mt-4 md:mt-6 text-center">
            <p className="text-gray-400 text-xs md:text-sm">
              {formData.role === 'staff' 
                ? 'Acces pentru personalul VGL Trans Express'
                : 'Acces restricționat doar pentru administratori autorizați'
              }
            </p>
          </div>
        </div>

        {/* Security Notice */}
        <div className="mt-4 md:mt-6 bg-white/5 backdrop-blur-sm rounded-xl md:rounded-2xl border border-white/10 p-3 md:p-4">
          <div className="flex items-start space-x-3">
            <Shield className="h-4 w-4 md:h-5 md:h-5 text-green-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs md:text-sm text-gray-300">
              <p className="font-medium text-white mb-1">Notă de securitate</p>
              <p>Toate activitățile sunt monitorizate și înregistrate. Utilizați doar credențialele autorizate pentru rolul dvs.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminLogin;